import {
  Button,
  Flex,
  Text
} from '@huspy/briks-web';
import { ProposalTemplate } from '@huspy/forge/templates';
import {
  Box, Skeleton, Stack, rem
} from '@mantine/core';
import briksTheme from '@shared/briks-theme';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { css } from '@huspy/styled-system/css';
import React, {
  useCallback, useEffect, useRef
} from 'react';
import useGetProposalById from '@modules/proposals/api/query/useGetProposalById';
import { createFormContext } from '@mantine/form';
import {
  ChevronsLeftRight,
  Download, Send, ZoomIn, ZoomOut
} from '@huspy/briks-icons';
import { ProposalCreationConfiguratorRoute } from '@modules/proposals/presentation/routes';
import { createPortal } from 'react-dom';
import useRequestDigitalProposalPdf from '@modules/proposals/api/mutation/useRequestDigitalProposalPdf';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { useTranslation } from 'react-i18next';
import { useDisclosure } from '@mantine/hooks';
import { ProposalEntity } from '@modules/clients/entities/client/api/dto/clientProposal.dto';
import { initProposalDownload } from '@modules/proposals/presentation/utils';
import { ProposalPreviewSlido } from '@modules/proposals/presentation/v2/pages/List/features/ProposalsTable/ae/features/ProposalPreviewSlido';
import { ProposalShareSlido } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/Configurator/components/ProposalShareSlido';
import { ProposalConfigurationSlido } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/Configurator/features/ProposalConfigurationSlido';
import { mapProposalEntityToTemplateData } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/Configurator/mappers/mapProposalEntityToTemplateData';

const boxStyles = css({
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: 'shadow-1',
  border: '1px solid rgba(24, 26, 24, 0.01)',
  backgroundColor: '#fff',
  margin: '0 auto',
});

export type ProposalConfigFormValues = {
  config: {
    showBankLogos: boolean;
    showRequiredDocuments: boolean;
    feesToShow: FeesToShow;
  };
  consultationFee: number;
};

export const [
  ProposalConfigFormProvider,
  useProposalConfigFormContext,
  useProposalConfigFormForm
] = createFormContext<ProposalConfigFormValues>();

const PROPOSAL_HEIGHT = 1600;

type FeesToShow = React.ComponentProps<typeof ProposalTemplate>['config']['feesToShow'];

export const ProposalConfiguratorPage = () => {
  const { id } = ProposalCreationConfiguratorRoute.useParams();
  const { data: proposalRawData, isLoading } = useGetProposalById(id);
  const data = mapProposalEntityToTemplateData(proposalRawData)!;
  const { t } = useTranslation();
  const { mutateAsync: downloadProposalPDF, isPending: isDownloadProposalPDFPending } = useRequestDigitalProposalPdf();
  const [isShareSlidoOpen, { open: openShareSlido, close: closeShareSlido }] = useDisclosure(false);
  const isAsideContentMounted = useRef<boolean>(false);

  const [isOpenPreviewSlido, { open: openPreviewSlido, close: closePreviewSlido }] = useDisclosure(false);

  const fileName = `${t('proposals.fileNamePrefix')} - ${data?.proposal.clientName} ${data?.proposal.clientLastName}.pdf`;

  // If location was not DIFC and user selects transfer_center_fee,
  // when updating to is DIFC, it will keep the transfer_center_fee
  // value. This line makes sure transfer_center_fee is not present
  // if DIFC
  const feesToShow = data.proposal.propertyDetails.location === 'DIFC'
    ? data.config.feesToShow.filter((f) => f !== 'transfer_center_fee')
    : data.config.feesToShow;

  const form = useProposalConfigFormForm({
    initialValues: {
      config: {
        ...data.config,
        feesToShow,
      },
      consultationFee: data.proposal.mortgageDetails.consultationFee,
    },
  });

  useEffect(() => {
    if (!isAsideContentMounted.current) {
      isAsideContentMounted.current = true;
    }

    return () => {
      isAsideContentMounted.current = false;
    };
  }, []);

  const handleDownloadProposal = useCallback(async () => {
    await initProposalDownload(
      fileName,
      'proposal-template',
      downloadProposalPDF
    );
    trackAmplitudeEvent(USER_EVENTS.PROPOSAL.DOWNLOADED);
  }, [downloadProposalPDF, fileName]);

  if (!data && !isLoading) {
    return 'No data...';
  }

  return (
    <>
      <ProposalPreviewSlido
        proposal={ proposalRawData as ProposalEntity }
        isOpen={ isOpenPreviewSlido }
        isInitiallyExpanded
        isExpandable={ false }
        hideFooter
        open={ openPreviewSlido }
        close={ closePreviewSlido }
      />

      <ProposalShareSlido
        close={ closeShareSlido }
        open={ openShareSlido }
        opened={ isShareSlidoOpen }
      />
      {
        isAsideContentMounted.current && createPortal(
          <Box w='100%' h='100%' mt='8'>
            <Text size='lg' weight='medium'>Quick actions</Text>
            <Flex direction='column' gap='2'>
              <Flex w='100%' mt='8' justifyContent='space-between' gap='2'>
                <Button
                  w='100%'
                  onClick={ openShareSlido }
                  data-test='share-proposal-btn'
                >
                  <Box w='16' h='16'>
                    <Send color='inherit' />
                  </Box>
                  Send
                </Button>
                <Button
                  w='100%'
                  disabled={ isDownloadProposalPDFPending }
                  loading={ isDownloadProposalPDFPending }
                  variant={ isDownloadProposalPDFPending ? 'outline' : 'primary' }
                  onClick={ handleDownloadProposal }
                  data-test='download-proposal-btn'
                >
                  <Box w='16' h='16'>
                    <Download color='inherit' />
                  </Box>
                  Download
                </Button>
              </Flex>
              <Button variant='tertiary' hidden>
                Create a case from this proposal
              </Button>
            </Flex>
          </Box>,
          document.querySelector('#asideContent')!
        )
      }
      <Box pos='relative'>
        <ProposalConfigFormProvider form={ form }>
          <ProposalConfigurationSlido />
          <Stack gap={ briksTheme.spacing[10] } pb={ briksTheme.spacing[20] }>
            <Stack gap={ briksTheme.spacing[2] }>
              <Text size='3xl' weight='medium' color='text-primary'>Review your proposal</Text>
              <Text
                size='md'
                weight='regular'
                color='text-secondary'
              >
                Congratulations! Your new proposal is here.
                Take a look at the proposal created from your preferences and selection
              </Text>
            </Stack>

            <Box
              w={ rem(787) }
              h={ rem(PROPOSAL_HEIGHT) }
              className={ boxStyles }
            >
              {isLoading ? (<Skeleton w='100%' h={ rem(1068) } radius='24px' />) : (
                <Box pos='relative'>
                  {!!data?.proposal?.bankOffers?.length && (
                    <TransformWrapper
                      initialScale={ 0.6 }
                      maxScale={ 4 }
                      minScale={ 0.4 }
                      centerZoomedOut
                      wheel={ { disabled: true } }
                    >
                      {({ zoomIn, zoomOut, resetTransform }) => (
                        <>
                          <Stack
                            gap={ briksTheme.spacing['0,5'] }
                            pos='absolute'
                            right={ 10 }
                            top={ 10 }
                            style={ { zIndex: 99 } }
                          >
                            <Button
                              type='button'
                              opacity={ 0.7 }
                              onClick={ () => zoomIn(0.3) }
                              size='sm'
                              variant='secondary'
                            >
                              <ZoomIn />
                            </Button>
                            <Button
                              type='button'
                              opacity={ 0.7 }
                              onClick={ () => zoomOut(0.3) }
                              size='sm'
                              variant='secondary'
                            >
                              <ZoomOut />
                            </Button>
                            <Button
                              opacity={ 0.7 }
                              type='button'
                              onClick={ () => resetTransform() }
                              size='sm'
                              variant='secondary'
                            >
                              Reset
                            </Button>
                            <Button
                              opacity={ 0.7 }
                              type='button'
                              onClick={ () => openPreviewSlido() }
                              size='sm'
                              variant='secondary'
                            >
                              <ChevronsLeftRight style={ { transform: 'rotate(45deg)' } } />
                            </Button>
                          </Stack>
                          <TransformComponent wrapperStyle={ {
                            maxWidth: '100%',
                            width: rem(787),
                            height: rem(PROPOSAL_HEIGHT),
                          } }
                          >
                            <ProposalTemplate
                              proposal={ {
                                ...data!.proposal!,
                                mortgageDetails: {
                                  ...data!.proposal!.mortgageDetails,
                                  consultationFee: form.values.consultationFee,
                                },
                              } }
                              brokerage={ data.brokerage }
                              config={ form.values.config }
                            />
                          </TransformComponent>
                        </>
                      )}
                    </TransformWrapper>
                  )}
                </Box>
              )}

            </Box>
          </Stack>
        </ProposalConfigFormProvider>
      </Box>
    </>
  );
};
